import { isUnaryOperator, isListOperator } from './filterPillValidation';
import { createQuickFilterAST } from './quickFilterPattern';
import { getUserTimezone, normalizeDateValue } from '../timezone';

/**
 * Convert filter pills and quick filter to AST
 * 
 * INVARIANT: This is the ONLY normalization point.
 * After this function, all date values MUST be {date: string, timezone: string}
 * 
 * Input: Date values can be strings (from relative dates, comma-separated input) or {date, timezone}
 * Output: All date values are canonical {date, timezone}
 * 
 * @param {Array<FilterPill>} filterPills - Array of filter pills
 * @param {string} quickFilter - Quick filter text
 * @param {Object} schema - Query schema (optional)
 * @returns {Object|null} Canonical AST or null
 */
export function filterPillsToAST(filterPills, quickFilter, schema) {
  // Normalize timezone ONCE at entry point
  const defaultTimezone = getUserTimezone();
  const predicates = [];
  
  // Assert function for date values after normalization
  const assertCanonicalDate = (value, context) => {
    if (!value || typeof value !== 'object' || !value.date || !value.timezone) {
      throw new Error(`${context}: Date value must be {date: string, timezone: string}, got ${typeof value}`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value.date)) {
      throw new Error(`${context}: Date must be YYYY-MM-DD format, got "${value.date}"`);
    }
  };
  
  // Ensure filterPills is an array
  const pills = Array.isArray(filterPills) ? filterPills : [];
  
  // Convert filter pills to predicates
  pills.forEach(pill => {
    const { field, operator } = pill;
    
    // Skip pills without values (except unary operators which don't need values)
    if (!isUnaryOperator(operator)) {
      // For list and binary operators, we need at least one value
      if (!pill.values || !Array.isArray(pill.values) || pill.values.length === 0) {
        return; // Skip this pill - no values selected
      }
    }
    
    // Handle UI-only between operator for date ranges
    // between never reaches the backend compiler; it is normalized here into
    // a canonical AND of >= start AND <= end predicates.
    if (operator === 'between') {
      const values = pill.values || [];
      if (Array.isArray(values) && values.length >= 2) {
        // Normalize both date values to canonical shape (throws on invalid)
        const startNormalized = normalizeDateValue(values[0], defaultTimezone);
        const endNormalized = normalizeDateValue(values[1], defaultTimezone);
        
        // Assert canonical shape
        assertCanonicalDate(startNormalized, 'between start date');
        assertCanonicalDate(endNormalized, 'between end date');
        
          predicates.push({
            type: 'and',
            items: [
              {
                type: 'predicate',
                field,
                op: '>=',
              value: startNormalized
              },
              {
                type: 'predicate',
                field,
                op: '<=',
              value: endNormalized
              }
            ]
          });
      }
    }
    // Handle unary operators
    else if (isUnaryOperator(operator)) {
      predicates.push({
        type: 'predicate',
        field,
        op: operator,
        value: null  // Unary operators have no value
      });
    }
    // Handle list operators
    else if (isListOperator(operator)) {
      // Ensure we have values (already checked above, but double-check)
      if (pill.values && Array.isArray(pill.values) && pill.values.length > 0) {
        const fieldMeta = schema?.fields?.find(f => f.name === field);
        const isDateField = fieldMeta?.kind?.toLowerCase() === 'date';
        
        // Normalize values based on field type
        const processedValues = pill.values.map((v, index) => {
          if (isDateField) {
            // Normalize date values to canonical shape
            const normalized = normalizeDateValue(v, defaultTimezone);
            assertCanonicalDate(normalized, `list date value at index ${index}`);
            return normalized;
          } else {
            // For non-date fields, extract IDs from user objects
          if (typeof v === 'object' && v !== null && v.id) {
            return v.id; // Use user ID for querying
          }
          return v;
          }
        });
        
        predicates.push({
          type: 'predicate',
          field,
          op: operator,
          value: processedValues
        });
      }
    }
    // Handle binary operators
    else {
      // Single value from array (already validated above)
      const value = pill.values[0];
      if (value !== null && value !== undefined && value !== '') {
        const fieldMeta = schema?.fields?.find(f => f.name === field);
        const isDateField = fieldMeta?.kind?.toLowerCase() === 'date';
        
        let processedValue = value;
        
        if (isDateField) {
          // Normalize date value to canonical shape
          processedValue = normalizeDateValue(value, defaultTimezone);
          assertCanonicalDate(processedValue, 'binary date value');
        } else {
          // For non-date fields, extract ID from user object if it's an object
        if (typeof value === 'object' && value !== null && value.id) {
          processedValue = value.id; // Use user ID for querying
          }
        }
        
        predicates.push({
          type: 'predicate',
          field,
          op: operator,
          value: processedValue
        });
      }
    }
  });
  
  // Add quick filter (exact pattern, order-independent)
  if (quickFilter && quickFilter.trim()) {
    const value = quickFilter.trim();
    const quickFilterAST = createQuickFilterAST(value);
    predicates.push(quickFilterAST);
  }
  
  // Combine predicates
  if (predicates.length === 0) return null;
  if (predicates.length === 1) return predicates[0];
  return { type: 'and', items: predicates };
}

